import { useContext, useState } from "react";
import {
  Container,
  Paper,
  Text,
  Title,
  List,
  ThemeIcon,
  rem,
  Image,
} from "@mantine/core";
import {
  IconCheck,
  IconCurrencyDollar,
  IconTrendingUp,
  IconWorld,
} from "@tabler/icons-react";

import "./styles.scss";
import { Colors, LogoFull, LogoFullWhite } from "../../../Lib/Data";
import Navbar from "../../../Components/Navbar";
import HomeNav from "../../Home/_nav";

const Benefits: React.FC = () => {
  const [theme, setTheme] = useState<"light" | "dark">("light");

  const benefits = [
    {
      title: "Save on USD Subscriptions",
      description:
        "International subscriptions are increasingly expensive. Our platform helps you lock in better rates and protect your subscription costs by providing access to premium services at a fraction of the cost.",
      icon: <IconCurrencyDollar style={{ width: rem(24), height: rem(24) }} />,
    },
    {
      title: "Rising Subscription Costs",
      description:
        "Since 2024, major streaming services have implemented multiple price increases. Netflix, Spotify, and other platforms have raised their prices globally, making it harder for users to maintain multiple subscriptions.",
      icon: <IconTrendingUp style={{ width: rem(24), height: rem(24) }} />,
    },
    {
      title: "Simple Digital Payments",
      description:
        "Paying for international services like Apple Music and iCloud can be complicated. Our platform solves this by providing straightforward payment options and handling the transactions for you.",
      icon: <IconWorld style={{ width: rem(24), height: rem(24) }} />,
    },
  ];

  const keyFeatures = [
    "Simple payment options in USD",
    "Bulk subscription discounts",
    "No hidden transaction fees",
    "Flexible subscription periods",
    "Automatic renewal management",
    "24/7 customer support",
    "Secure payment processing",
    "Multiple service bundling",
  ];

  return (
    <>
      <HomeNav useBlogColorScheme={true} />

      <Container size="lg" className="benefits-container">
        <div className="hero-section">
          <div className="hero-content">
            <img src={LogoFull} alt="Manysubs Logo" className="hero-logo" />
            <Text
              fz={30}
              className="main-title"
              c={theme === "dark" ? Colors.White : Colors.BlackPrimary}
            >
              Why Choose Manysubs?
            </Text>
            <Text
              size="md"
              className="subtitle"
              c={
                theme === "dark"
                  ? Colors.WhiteBlueTransparent
                  : Colors.BluePrimary
              }
            >
              Your solution to affordable digital subscriptions in Nigeria
            </Text>
          </div>
        </div>

        <div className="benefits-grid">
          {benefits.map((benefit, index) => (
            <Paper
              key={index}
              className={`benefit-card card-${theme}`}
              shadow="sm"
              p="xl"
            >
              <ThemeIcon
                size={50}
                radius="md"
                color={Colors.BluePrimary}
                className="benefit-icon"
              >
                {benefit.icon}
              </ThemeIcon>
              <Title
                order={3}
                className="benefit-title"
                c={theme === "dark" ? Colors.White : Colors.BlackPrimary}
              >
                {benefit.title}
              </Title>
              <Text
                c={
                  theme === "dark"
                    ? Colors.WhiteBlueTransparent
                    : Colors.BlackPrimary
                }
              >
                {benefit.description}
              </Text>
            </Paper>
          ))}
        </div>

        <Paper className={`features-section card-${theme}`} p="xl" mt="xl">
          <Title
            order={2}
            mb="lg"
            c={theme === "dark" ? Colors.White : Colors.BlackPrimary}
          >
            Key Benefits
          </Title>
          <List
            spacing="md"
            size="lg"
            center
            icon={
              <ThemeIcon color={Colors.GreenPrimary} size={24} radius="xl">
                <IconCheck style={{ width: rem(16), height: rem(16) }} />
              </ThemeIcon>
            }
          >
            {keyFeatures.map((feature, index) => (
              <List.Item
                key={index}
                c={
                  theme === "dark"
                    ? Colors.WhiteBlueTransparent
                    : Colors.BlackPrimary
                }
              >
                {feature}
              </List.Item>
            ))}
          </List>
        </Paper>

        <Paper className={`savings-section card-${theme}`} p="xl" mt="xl">
          <Title
            order={2}
            mb="lg"
            c={theme === "dark" ? Colors.White : Colors.BlackPrimary}
          >
            Real Savings
          </Title>
          <Text
            size="lg"
            c={
              theme === "dark"
                ? Colors.WhiteBlueTransparent
                : Colors.BlackPrimary
            }
          >
            With rising subscription costs, our platform helps you save significantly. 
            For example, individual premium plans that used to be affordable are now 
            increasingly expensive when managed separately. Our bulk subscription 
            plans help you maintain your digital lifestyle without breaking the bank.
          </Text>
          <Text
            size="lg"
            mt="md"
            c={
              theme === "dark"
                ? Colors.WhiteBlueTransparent
                : Colors.BlackPrimary
            }
          >
            Additionally, services like Apple Music and iCloud that require
            specific payment methods are now easily accessible through our
            platform, eliminating the need for complex payment processes or 
            unreliable cards.
          </Text>
        </Paper>
      </Container>
    </>
  );
};

export default Benefits;
